import { useMe } from "@/api/services/admin/auth";
import { EAdminUserRole, EMenuId } from "@/constants/enum";
import { menus } from "@/constants/menu";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { AppRoutes } from "@/routes/config";
import { background, black, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { TMenu } from "@/types/menu";
import { hexToRgba } from "@/utils/pieces";
import { Divider, Drawer, Stack, Tooltip, Typography, styled } from "@mui/material";
import Box from "@mui/material/Box";
import zIndex from "@mui/material/styles/zIndex";
import { FC, useMemo } from "react";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";

type TAppDrawerProps = {
  isOpenDrawer: boolean;
  toggleDrawer: () => void;
};

const SUPER_ADMIN_MENUS: string[] = [EMenuId.SYSTEM_MANAGEMENT];

export const AppDrawer: FC<TAppDrawerProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const { pathname } = useLocation();
  const { data: me } = useMe();

  const isSuperAdmin = me?.data?.role === EAdminUserRole.SUPER_ADMIN;

  const filteredMenus = useMemo(() => menus.filter((item) => !item.id || !SUPER_ADMIN_MENUS.includes(item.id) || isSuperAdmin), [isSuperAdmin]);

  const DrawerContent = useMemo(
    () => (
      <ListWrapper isLaptop={isLaptop} gap={1}>
        <Stack gap={1.5}>
          {filteredMenus.map((item, index) => {
            const isLast = index === filteredMenus.length - 1;
            return (
              <Stack gap={2}>
                <MenuItem key={item.text} {...item} />
                {!isLast && <Divider color={background.white} sx={{ opacity: 0.3 }} />}
              </Stack>
            );
          })}
        </Stack>
        {/* <Logo>
          <img src={sidebarLogo} alt="logo" />
        </Logo> */}
      </ListWrapper>
    ),
    [pathname, isLaptop, filteredMenus],
  );

  if (!isLaptop)
    return (
      <ListWrapper
        isLaptop={isLaptop}
        gap={1}
        sx={{
          position: "fixed",
          top: "57px",
          overflow: "auto",
          height: "calc(100% - 57px)",
        }}
      >
        <Stack gap={1.5}>
          {filteredMenus.map((item, index) => {
            const isLast = index === filteredMenus.length - 1;
            return (
              <Stack gap={2}>
                <MenuItem key={item.text} {...item} />
                {!isLast && <Divider color={background.white} sx={{ opacity: 0.3 }} />}
              </Stack>
            );
          })}
        </Stack>
        {/* <Logo>
          <img src={sidebarLogo} alt="logo" />
        </Logo> */}
      </ListWrapper>
    );
  return (
    <Drawer
      open={isOpenDrawer}
      onClose={toggleDrawer}
      hideBackdrop
      ModalProps={{ sx: { width: "fit-content" } }}
      PaperProps={{ sx: { bgcolor: background.main, height: "calc(100vh - 57px)", top: "57px", backgroundColor: black[30] } }}
    >
      {/* <IconCloseWrapper>
        <IconCloseBtn onClick={toggleDrawer} />
      </IconCloseWrapper> */}
      {DrawerContent}
    </Drawer>
  );
};

const MenuItem: FC<TMenu> = (props) => {
  const { isLaptop } = useMediaQuery();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isActive = props.relatedRoutes?.some((route) => Boolean(matchRoutes([{ path: route }], pathname)));

  const childrenContent = useMemo(() => {
    return props.children?.map((item) => <MenuItem key={item.text} {...item} />);
  }, [props.children]);

  const content = useMemo(() => {
    const Icon = props.icon;
    return (
      <Stack gap={props?.children ? 1 : 0}>
        <Stack
          p={isLaptop && !Icon ? 0 : 1}
          pl={isLaptop ? 1 : props.path ? 2 : 1}
          direction="row"
          alignItems="center"
          gap={1.5}
          justifyContent={isLaptop ? "center" : "inherit"}
        >
          {Icon && <Icon fontSize={24} color={text.white} />}
          {!isLaptop && (
            <Typography variant={props.path ? "body14Bold" : "cap12Bold"} color={text.white}>
              {props.text}
            </Typography>
          )}
        </Stack>
        <Stack gap={1}>{childrenContent}</Stack>
      </Stack>
    );
  }, [props.text, props.icon]);

  if (!props.path) return content;
  if (isLaptop)
    return (
      <Tooltip title={props.text} placement="right">
        <ListItemWrapper isLaptop={isLaptop} className={isActive ? "active" : ""} onClick={() => navigate(props.path ?? AppRoutes.home)}>
          {content}
        </ListItemWrapper>
      </Tooltip>
    );
  return (
    <ListItemWrapper isLaptop={isLaptop} className={isActive ? "active" : ""} onClick={() => navigate(props.path ?? AppRoutes.home)}>
      {content}
    </ListItemWrapper>
  );
};

const ListWrapper = styled(Stack)<{ isLaptop: boolean }>`
  padding: ${spacing.md} ${spacing.sm};
  width: ${({ isLaptop }) => (isLaptop ? "80px" : "auto")};
  min-width: ${({ isLaptop }) => (isLaptop ? "auto" : "242px")};
  background-color: ${black[30]};
  color: ${text.white};
  display: flex;
  flex-direction: column;
  position: sticky;
  z-index: ${zIndex.appBar};
`;

const ListItemWrapper = styled(Box)<{ isLaptop: boolean }>`
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: ${rounded.sm};
  &.active {
    background-color: ${hexToRgba(background.white, 0.3)} !important;
  }

  &:hover {
    background-color: ${hexToRgba(background.white, 0.1)};
  }
`;
